import { createContext, useContext, useLayoutEffect, useState, useCallback, useEffect, useRef } from 'react';
import { getMe, patchMeTheme } from '@/api';
import { AUTH_EVENT, getToken, isLoggedIn, setToken } from '@/auth/token';
import {
  applyDocumentTheme,
  parseTheme,
  readStoredTheme,
  writeStoredTheme,
  type Theme,
} from './preference';

type ThemeContextValue = {
  theme: Theme;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  toggleTheme: () => {},
});

function persistAccount(theme: Theme) {
  if (!isLoggedIn()) return;
  patchMeTheme(theme)
    .then((u) => {
      const tok = getToken();
      if (tok) setToken(tok, u);
    })
    .catch(() => { /* keep local preference */ });
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => readStoredTheme());
  const themeRef = useRef(theme);
  const seededRef = useRef(false);
  themeRef.current = theme;

  useLayoutEffect(() => {
    applyDocumentTheme(theme);
    writeStoredTheme(theme);
  }, [theme]);

  const hydrateFromAccount = useCallback(() => {
    if (!isLoggedIn()) {
      seededRef.current = false;
      return;
    }
    getMe()
      .then((u) => {
        const tok = getToken();
        if (tok) setToken(tok, u);
        const account = parseTheme(u.theme);
        if (account) {
          seededRef.current = true;
          setTheme(account);
          return;
        }
        if (!seededRef.current) {
          seededRef.current = true;
          persistAccount(themeRef.current);
        }
      })
      .catch(() => { /* stay on local preference */ });
  }, []);

  useEffect(() => {
    hydrateFromAccount();
    window.addEventListener(AUTH_EVENT, hydrateFromAccount);
    return () => window.removeEventListener(AUTH_EVENT, hydrateFromAccount);
  }, [hydrateFromAccount]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => {
      const next = t === 'dark' ? 'light' : 'dark';
      persistAccount(next);
      return next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
